import { genXAPIValidData } from '@vedalib/courseAnalitycs'
import { ISendXapiStatement } from '@vedalib/courseAnalitycs/src'
import type { Block } from '@vedalib/editor'
import type {
  AccordionElementValue,
  AudioElementValue,
  ButtonElementValue,
  CardElementValue,
  ChartElementValue,
  CheckboxElementValue,
  CodeElementValue,
  CourseProgressElementValue,
  CourseStructureElementValue,
  EditorElement,
  ElementValue,
  EmbeddedElementValue,
  GroupingElementValue,
  LabeledGraphicElementValue,
  LayoutListElementValue,
  LinkElementValue,
  ListElementValue,
  RectangleElementValue,
  ReflectionElementValue,
  RichTextElementValue,
  SimulatorElementValue,
  SliderElementValue,
  SplitterElementValue,
  TableElementValue,
  TabsElementValue,
  TestFeedbackElementValue,
  TestGeneralFeedbackElementValue,
  TestGroupingElementValue,
  TestHotspotElementValue,
  TestInfoEndElementValue,
  TestInfoStartElementValue,
  TestKeyboardInputElementValue,
  TestMatchingElementValue,
  TestMultipleChoiceElementValue,
  TestRankingElementValue,
  TestResultElementValue,
  TestSingleChoiceElementValue,
  VideoElementValue,
} from '@vedalib/editor/lib/elements'
import { CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'
import { rtValueToText } from '@vedalib/rich-text'

import { sendCourseAnalytics } from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getCompanyId, getProgress, getProjectId } from 'services/Store/Project/selectors'

import { FEATURE_FLAGS } from '../config'
import { useGetElementLink } from './useGetElementLink'

interface IUseGetSetViewedEl {
  element?: EditorElement | null
  block?: Block
}

interface ISetViewedElement {
  inViewPort?: boolean
  startTime?: string
  endTime?: string
}

type ElementValueTypes = {
  [CONFIG_ELEMENTS.table.type]: TableElementValue
  [CONFIG_ELEMENTS.richText.type]: RichTextElementValue
  [CONFIG_ELEMENTS.image.type]: ElementValue
  [CONFIG_ELEMENTS.list.type]: ListElementValue
  [CONFIG_ELEMENTS.checkbox.type]: CheckboxElementValue
  [CONFIG_ELEMENTS.card.type]: CardElementValue
  [CONFIG_ELEMENTS.tabs.type]: TabsElementValue
  [CONFIG_ELEMENTS.grouping.type]: GroupingElementValue
  [CONFIG_ELEMENTS.slider.type]: SliderElementValue
  [CONFIG_ELEMENTS.accordion.type]: AccordionElementValue
  [CONFIG_ELEMENTS.video.type]: VideoElementValue
  [CONFIG_ELEMENTS.audio.type]: AudioElementValue
  [CONFIG_ELEMENTS.labeledGraphic.type]: LabeledGraphicElementValue
  [CONFIG_ELEMENTS.code.type]: CodeElementValue
  [CONFIG_ELEMENTS.splitter.type]: SplitterElementValue
  [CONFIG_ELEMENTS.downloader.type]: ElementValue
  [CONFIG_ELEMENTS.link.type]: LinkElementValue
  [CONFIG_ELEMENTS.simulator.type]: SimulatorElementValue
  [CONFIG_ELEMENTS.button.type]: ButtonElementValue
  [CONFIG_ELEMENTS.testMultipleChoice.type]: TestMultipleChoiceElementValue
  [CONFIG_ELEMENTS.testSingleChoice.type]: TestSingleChoiceElementValue
  [CONFIG_ELEMENTS.testKeyboardInput.type]: TestKeyboardInputElementValue
  [CONFIG_ELEMENTS.testMatching.type]: TestMatchingElementValue
  [CONFIG_ELEMENTS.testRanking.type]: TestRankingElementValue
  [CONFIG_ELEMENTS.testHotspot.type]: TestHotspotElementValue
  [CONFIG_ELEMENTS.courseStructure.type]: CourseStructureElementValue
  [CONFIG_ELEMENTS.embedded.type]: EmbeddedElementValue
  [CONFIG_ELEMENTS.layoutList.type]: LayoutListElementValue
  [CONFIG_ELEMENTS.testFeedback.type]: TestFeedbackElementValue
  [CONFIG_ELEMENTS.reflection.type]: ReflectionElementValue
  [CONFIG_ELEMENTS.testGeneralFeedback.type]: TestGeneralFeedbackElementValue
  [CONFIG_ELEMENTS.rectangle.type]: RectangleElementValue
  [CONFIG_ELEMENTS.chart.type]: ChartElementValue
  [CONFIG_ELEMENTS.courseProgress.type]: CourseProgressElementValue
  [CONFIG_ELEMENTS.divider.type]: ElementValue
  [CONFIG_ELEMENTS.testInfoEnd.type]: TestInfoEndElementValue
  [CONFIG_ELEMENTS.testInfoStart.type]: TestInfoStartElementValue
  [CONFIG_ELEMENTS.testResult.type]: TestResultElementValue
  [CONFIG_ELEMENTS.testGrouping.type]: TestGroupingElementValue
}

interface IGetXAPIDataConfig<T extends keyof ElementValueTypes> {
  inViewPort?: boolean
  elementType: T
  elementId: string
  startTime?: string
  endTime?: string
  elementUrl?: string
  value?: ElementValueTypes[T]
  blockId?: string
  sectionId?: string
  companyId?: string
  projectId?: string
  progress?: number
}

const getXAPIDataConfigExperienced = <T extends keyof ElementValueTypes>({
  elementType,
  startTime,
  endTime,
  inViewPort = false,
  elementUrl,
  elementId,
  blockId = '',
  sectionId = '',
  companyId = '',
  projectId = '',
  value,
  progress = 0,
}: IGetXAPIDataConfig<T>): ISendXapiStatement | undefined => {
  const baseObjectExtensions = {
    elementUrl,
    blockId,
    sectionId,
    elementId,
    companyId,
    projectId,
    progress,
  }
  const baseResultExtensions = { endTime, inViewPort, startTime }
  const handlers: {
    [K in keyof ElementValueTypes]: (value: ElementValueTypes[K]) => ISendXapiStatement | undefined
  } = {
    [CONFIG_ELEMENTS.table.type]: (tableValue) => {
      const { cells, columns } = tableValue
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.table.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.table.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: {
            columnsCount: columns.length,
            rowsCount: cells.length,
            ...baseObjectExtensions,
          },
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.richText.type]: (richTextValue) => {
      const wordCount = rtValueToText(richTextValue).length
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.richText.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.richText.type,
          resultExtensions: { ...baseResultExtensions, wordCount },
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.image.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.image.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.image.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.list.type]: (listValue) => {
      const itemsCount = listValue.items.length
      const wordCount = listValue.items.reduce(
        (acc, item) => acc + rtValueToText(item.label).length,
        0,
      )
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.list.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.list.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: {
            itemsCount,
            wordCount,
            ...baseObjectExtensions,
          },
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.checkbox.type]: (checkboxValue) => {
      const checkboxItemsCount = checkboxValue.items.length
      const checkboxWordCountList = checkboxValue.items.reduce(
        (acc, item) => acc + rtValueToText(item.label).length,
        0,
      )
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.checkbox.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.checkbox.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: {
            totalItems: checkboxItemsCount,
            totalWords: checkboxWordCountList,
            ...baseObjectExtensions,
          },
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.card.type]: (cardValue) => {
      const cardBackWordCount = rtValueToText(cardValue.back.label).length
      const cardFrontWordCount = rtValueToText(cardValue.front.label).length
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.card.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.card.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: {
            totalWords: cardBackWordCount + cardFrontWordCount,
            ...baseObjectExtensions,
          },
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.tabs.type]: (tabsValue) => {
      const tabsItems = tabsValue.items
      const tabsItemsCount = tabsItems.length
      const tabsWordCount = tabsItems.reduce(
        (acc, item) => acc + rtValueToText(item.label).length,
        0,
      )
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.tabs.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.tabs.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: {
            itemCount: tabsItemsCount,
            wordCount: tabsWordCount,
            ...baseObjectExtensions,
          },
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.grouping.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.grouping.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.grouping.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.slider.type]: (sliderValue) => {
      const sliderItems = sliderValue.items
      const sliderItemsCount = sliderItems.length
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.slider.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.slider.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: { slideCount: sliderItemsCount, ...baseObjectExtensions },
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.accordion.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.accordion.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.accordion.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.video.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.video.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.video.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.audio.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.audio.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.audio.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.labeledGraphic.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.labeledGraphic.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.labeledGraphic.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.code.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.code.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.code.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.splitter.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.splitter.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.splitter.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.downloader.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.downloader.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.downloader.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.link.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.link.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.link.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.simulator.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.simulator.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.simulator.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.button.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.button.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.button.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.testMultipleChoice.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testMultipleChoice.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.testMultipleChoice.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.testSingleChoice.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testSingleChoice.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.testSingleChoice.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.testKeyboardInput.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testKeyboardInput.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.testKeyboardInput.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.testMatching.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testMatching.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.testMatching.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.testRanking.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testRanking.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.testRanking.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.testHotspot.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testHotspot.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.testHotspot.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.courseStructure.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.courseStructure.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.courseStructure.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.rectangle.type]: () => {
      return undefined
    },
    [CONFIG_ELEMENTS.chart.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.chart.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.chart.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.courseProgress.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.courseProgress.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.courseProgress.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.divider.type]: () => {
      return undefined
    },
    [CONFIG_ELEMENTS.embedded.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.embedded.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.embedded.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.layoutList.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.layoutList.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.layoutList.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.testFeedback.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testFeedback.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.testFeedback.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.testInfoEnd.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testInfoEnd.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.testInfoEnd.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.testInfoStart.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testInfoStart.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.testInfoStart.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.testResult.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testResult.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.testResult.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.reflection.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.reflection.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.reflection.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
    [CONFIG_ELEMENTS.testGeneralFeedback.type]: () => {
      return undefined
    },
    [CONFIG_ELEMENTS.testGrouping.type]: () => {
      return genXAPIValidData({
        elementType: CONFIG_ELEMENTS.testGrouping.type,
        verb: 'experienced',
        data: {
          elementId,
          elementType: CONFIG_ELEMENTS.testGrouping.type,
          resultExtensions: baseResultExtensions,
          objectExtensions: baseObjectExtensions,
        },
      }).xAPIData
    },
  }

  const handler = handlers[elementType]

  return handler(value!)
}

export const useGetSetViewedEl = ({ element, block }: IUseGetSetViewedEl) => {
  const elementUrl = useGetElementLink(block?.uuid || '')
  const dispatch = useProjectDispatch()
  const companyId = useProjectContext(getCompanyId)
  const projectId = useProjectContext(getProjectId)
  const progress = useProjectContext(getProgress) ?? 0
  const sectionId = block?.sectionId || ''

  const setViewedElement = (params: ISetViewedElement) => {
    if (!element || !FEATURE_FLAGS.viewedElement) {
      return
    }

    const { type: elementType, id: elementId, value } = element

    const xAPIData = getXAPIDataConfigExperienced({
      ...params,
      elementType,
      elementId,
      value,
      elementUrl,
      sectionId,
      blockId: block?.uuid || '',
      companyId,
      projectId,
      progress,
    })

    if (!xAPIData) {
      return
    }

    if (FEATURE_FLAGS.viewedElement) {
      dispatch(sendCourseAnalytics(xAPIData))
    }
  }
  return { setViewedElement }
}
